import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Bell, Shield, Search, GitPullRequest, Loader2, BarChart3, Activity, Bot,
} from "lucide-react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar as RechartsRadar,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import {
  useSentinelRules, useSentinelAlerts, useSentinelAudits, useSentinelProposals,
  useSentinelScores, useSentinelIntelligence, useRunSentinelAgent,
} from "@/hooks/useSentinelCycle";
import {
  AGENTS, RECOMMENDATION_ICONS, RECOMMENDATION_COLORS, PIE_COLORS,
} from "./sentinel-constants";
import { SentinelHealthPanel } from "./SentinelHealthPanel";
import { SentinelUtilityProof } from "./SentinelUtilityProof";

type Score = { dimension: string; score: number };

interface Props {
  orgId?: string;
  onTabChange?: (tab: string) => void;
}

export default function SentinelOverview({ orgId, onTabChange }: Props) {
  const { data: rules = [] } = useSentinelRules();
  const { data: alerts = [] } = useSentinelAlerts();
  const { data: audits = [] } = useSentinelAudits();
  const { data: proposals = [] } = useSentinelProposals();
  const { data: scores = [] } = useSentinelScores();
  const { data: intelligence, isLoading: loadingIntel } = useSentinelIntelligence();
  const runAgent = useRunSentinelAgent();

  const openAlerts = alerts.filter((a) => a.status === "open").length;
  const pendingProposals = proposals.filter((p) => p.state === "draft" || p.state === "pending").length;
  const activeRules = rules.filter((r) => r.active).length;

  const kpis = [
    { label: "Regras Ativas", value: activeRules, icon: Shield, color: "text-primary", tab: "config" },
    { label: "Alertas Abertos", value: openAlerts, icon: Bell, color: openAlerts > 0 ? "text-destructive" : "text-success", tab: "alerts" },
    { label: "Auditorias", value: audits.length, icon: Search, color: "text-info", tab: "audits" },
    { label: "Propostas Pendentes", value: pendingProposals, icon: GitPullRequest, color: "text-warning", tab: "proposals" },
  ];

  // Radar data
  const radarData = (() => {
    const dims: Record<string, number[]> = {};
    (scores as Score[]).slice(0, 100).forEach((s) => {
      if (!dims[s.dimension]) dims[s.dimension] = [];
      dims[s.dimension].push(s.score);
    });
    return Object.entries(dims).map(([dim, vals]) => ({
      dimension: dim === "prompt_health" ? "Prompt Health" : dim === "model_fit" ? "Model Fit" : dim === "quality" ? "Quality" : dim,
      score: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10,
      fullMark: 10,
    }));
  })();

  const severityPieData = intelligence?.severity_distribution
    ? [
        { name: "Crítico", value: intelligence.severity_distribution.critical },
        { name: "Alto", value: intelligence.severity_distribution.high },
        { name: "Médio", value: intelligence.severity_distribution.medium },
        { name: "Baixo", value: intelligence.severity_distribution.low },
      ].filter((d) => d.value > 0)
    : [];

  const recommendations = intelligence?.recommendations ?? [];
  const timeline = intelligence?.timeline ?? [];

  return (
    <div className="space-y-6">
      {/* Cycle health */}
      <SentinelHealthPanel />
      <SentinelUtilityProof />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <motion.div
            key={k.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <button
              type="button"
              onClick={() => onTabChange?.(k.tab)}
              className="w-full text-left"
              aria-label={`Ver ${k.label}`}
            >
              <Card className="hover:border-primary/40 transition-colors cursor-pointer">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{k.label}</span>
                    <k.icon className={`w-4 h-4 ${k.color}`} aria-hidden="true" />
                  </div>
                  <div className="text-2xl font-bold font-headline text-foreground">{k.value}</div>
                </CardContent>
              </Card>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" aria-hidden="true" />
              Score por Dimensão
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {radarData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Sem dados de score ainda. Execute uma auditoria.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                  <RechartsRadar dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" aria-hidden="true" />
              Distribuição de Severidade
            </CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {loadingIntel ? (
              <Skeleton className="h-full w-full" />
            ) : severityPieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                Nenhum alerta categorizado.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={severityPieData} dataKey="value" nameKey="name" outerRadius={80} label>
                    {severityPieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Timeline (7 dias)</CardTitle>
          </CardHeader>
          <CardContent className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                <RTooltip />
                <Area type="monotone" dataKey="alerts" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.2} />
                <Area type="monotone" dataKey="resolved" stroke="hsl(var(--success))" fill="hsl(var(--success))" fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Recomendações</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loadingIntel ? (
            <>
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </>
          ) : recommendations.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma recomendação no momento.</p>
          ) : (
            recommendations.slice(0, 5).map((rec, i) => {
              const Icon = RECOMMENDATION_ICONS[rec.type] || RECOMMENDATION_ICONS.info;
              const color = RECOMMENDATION_COLORS[rec.type] || RECOMMENDATION_COLORS.info;
              return (
                <div key={rec.id ?? i} className={`p-3 rounded-md border ${color}`}>
                  <div className="flex items-start gap-2">
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-foreground">{rec.title}</div>
                      <div className="text-xs text-muted-foreground">{rec.description}</div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Mini agent grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" aria-hidden="true" />
            Agentes Sentinel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {AGENTS.map((agent) => {
              const isRunning = runAgent.isPending && runAgent.variables?.agentId === agent.id;
              return (
                <Button
                  key={agent.id}
                  variant="outline"
                  size="sm"
                  className="h-auto py-2 flex-col gap-1"
                  disabled={isRunning}
                  onClick={() => {
                    runAgent.mutate({
                      agentId: agent.id,
                      org_id: orgId,
                      auditId: audits[0]?.id,
                      alertId: alerts[0]?.id,
                    });
                  }}
                >
                  {isRunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <agent.icon className={`w-4 h-4 ${agent.color}`} aria-hidden="true" />
                  )}
                  <span className="text-[11px]">{agent.name}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
